import { LocalSorter } from './local.sorter';
import { LocalFilter } from './local.filter';
import { LocalPager } from './local.pager';
import { DataSource } from '../data-source';
import { deepExtend } from '../../helpers';

export class LocalDataSource extends DataSource {
  protected uidCounter = -1;
  protected data: any[] = [];
  protected filteredAndSorted: any[] = [];
  protected sortConf: any[] = [];
  protected filterConf: any = {
    filters: [],
    andOperator: true
  };
  protected pagingConf: any = {};

  constructor(data: any[] = []) {
    super();

    data.forEach((record) => {
      record['uid'] = this.getNewUid();
    });
    this.data = data;
  }

  public getNewUid() {
    this.uidCounter = this.uidCounter + 1;
    return this.uidCounter + 1;
  }

  public load(data: any[]): Promise<any> {
    data.forEach((record) => {
      record['uid'] = this.getNewUid();
    });
    this.data = data;

    return super.load(data);
  }

  public getData(): any[] {
    return this.data;
  }

  public setData(data: any[]) {
    this.data = data;
  }

  public prepend(element: any): Promise<any> {
    this.reset(true);
    this.data.unshift(element);
    return super.prepend(element);
  }

  public insert(position: number, element: any): Promise<any> {
    element['uid'] = this.getNewUid();
    this.data.splice(position, 0, element);
    return super.prepend(element);
  }

  public append(element: any): Promise<any> {
    this.reset(true);

    this.data.push(element);
    return super.append(element);
  }

  public add(element: any): Promise<any> {
    this.data.push(element);

    return super.add(element);
  }

  public remove(element: any): Promise<any> {
    this.data = this.data.filter((el) => el.uid !== element.uid);

    return super.remove(element);
  }

  public removeNewRow(element: any): Promise<any> {
    this.data = this.data.filter((el) => el.uid !== element.uid);

    return super.removeNewRow(element);
  }

  public update(element: any, values: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.find(element)
        .then((found) => {
          found = deepExtend(found, values);
          super
            .update(found, values)
            .then(resolve)
            .catch(reject);
        })
        .catch(reject);
    });
  }

  public find(element: any): Promise<any> {
    const found = this.data.find((el) => {
      return el.values === element.values;
    });
    if (found) {
      return Promise.resolve(found);
    }

    return Promise.reject(new Error('Element was not found in the dataset'));
  }

  public getElements(): Promise<any> {
    const data = this.data.slice(0);
    return Promise.resolve(this.prepareData(data));
  }

  public getElementsWithCustomFilter(): Promise<any> {
    const data = this.data.slice(0);
    return Promise.resolve(this.prepareDataWithCustomFilter(data));
  }

  public getFilteredAndSorted(): Promise<any> {
    let data = this.data.slice(0);
    let isFilterPresent = false;
    this.filterConf.filters.forEach((field) => {
      if (field.search && field.search !== '') {
        isFilterPresent = true;
      }
    });
    if (isFilterPresent) {
      this.prepareDataWithCustomFilter(data);
    } else {
      this.prepareData(data);
    }
    return Promise.resolve(this.filteredAndSorted);
  }

  public getAll(): Promise<any> {
    const data = this.data.slice(0);
    return Promise.resolve(this.data);
  }

  public reset(silent = false) {
    if (silent) {
      this.filterConf = {
        filters: [],
        andOperator: true
      };
      this.sortConf = [];
      this.pagingConf['page'] = 1;
    } else {
      this.setFilter([], true, false);
      this.setSort([], false);
      this.setPage(1);
    }
  }

  public empty(): Promise<any> {
    this.data = [];

    return super.empty();
  }

  public count(): number {
    return this.filteredAndSorted.length;
  }

  /**
   *
   * Array of conf objects
   * [
   *  {field: string, direction: asc|desc|null, compare: Function|null},
   * ]
   * @param conf
   * @param doEmit
   * @returns {LocalDataSource}
   */
  public setSort(conf: any[], doEmit = true): LocalDataSource {
    if (conf !== null) {
      conf.forEach((fieldConf) => {
        if (
          !fieldConf['field'] ||
          typeof fieldConf['direction'] === 'undefined'
        ) {
          throw new Error('Sort configuration object is not valid');
        }
      });
      this.sortConf = conf;
    }

    super.setSort(conf, doEmit);
    return this;
  }

  /**
   *
   * Array of conf objects
   * [
   *  {field: string, search: string, filter: Function|null},
   * ]
   * @param conf
   * @param andOperator
   * @param doEmit
   * @returns {LocalDataSource}
   */
  public setFilter(
    conf: any[],
    andOperator = true,
    doEmit = true
  ): LocalDataSource {
    if (conf && conf.length > 0) {
      conf.forEach((fieldConf) => {
        this.addFilter(fieldConf, andOperator, false);
      });
    } else {
      this.filterConf = {
        filters: [],
        andOperator: true
      };
    }
    this.filterConf.andOperator = andOperator;
    this.pagingConf['page'] = 1;

    super.setFilter(conf, andOperator, doEmit);
    return this;
  }

  public addFilter(
    fieldConf: any,
    andOperator = true,
    doEmit: boolean = true
  ): LocalDataSource {
    if (!fieldConf['field'] || typeof fieldConf['search'] === 'undefined') {
      throw new Error('Filter configuration object is not valid');
    }

    let found = false;
    this.filterConf.filters.forEach((currentFieldConf: any, index: any) => {
      if (currentFieldConf['field'] === fieldConf['field']) {
        this.filterConf.filters[index] = fieldConf;
        found = true;
      }
    });
    if (!found) {
      this.filterConf.filters.push(fieldConf);
    }
    this.filterConf.andOperator = andOperator;
    super.addFilter(fieldConf, andOperator, doEmit);
    return this;
  }

  public setPaging(
    page: number,
    perPage: number,
    doEmit: boolean = true
  ): LocalDataSource {
    this.pagingConf['page'] = page;
    this.pagingConf['perPage'] = perPage;

    super.setPaging(page, perPage, doEmit);
    return this;
  }

  public setPage(page: number, doEmit: boolean = true): LocalDataSource {
    this.pagingConf['page'] = page;
    super.setPage(page, doEmit);
    return this;
  }

  public getSort(): any {
    return this.sortConf;
  }

  public getFilter(): any {
    return this.filterConf;
  }
  public getFilterPromise(): Promise<any> {
    return Promise.resolve(this.filterConf);
  }

  public getPaging(): any {
    return this.pagingConf;
  }

  protected prepareData(data: any[]): any[] {
    data = this.filter(data);
    data = this.sort(data);
    this.filteredAndSorted = data.slice(0);

    return this.paginate(data);
  }

  protected prepareDataWithCustomFilter(data: any[]): any[] {
    let pagingConf = this.getPaging();
    let page = pagingConf['page'];
    let perPage = pagingConf['perPage'];
    let addRecordsData = data.filter((el) => {
      if (el['isNewRow']) {
        return true;
      }
    });
    data = this.filter(data);
    data = this.sort(data);
    let isFilterPresent = false;
    this.filterConf.filters.forEach((field) => {
      if (field.search && field.search !== '') {
        isFilterPresent = true;
      }
    });
    if (isFilterPresent) {
      if (addRecordsData.length > 0) {
        data = data.filter((el) => el['isNewRow'] !== true);
      }
      addRecordsData = addRecordsData.reverse();
      let position = 0;
      addRecordsData.forEach((addRecord, index, array) => {
        position = perPage * (page - 1);
        data.splice(position, 0, addRecord);
        position++;
      });
    }
    this.filteredAndSorted = data.slice(0);
    return this.paginate(data);
  }

  protected sort(data: any[]): any[] {
    if (this.sortConf) {
      this.sortConf.forEach((fieldConf) => {
        data = LocalSorter.sort(
          data,
          fieldConf['field'],
          fieldConf['direction'],
          fieldConf['compare']
        );
      });
    }
    return data;
  }

  // TODO: refactor?
  protected filter(data: any[]): any[] {
    if (this.filterConf.filters) {
      if (this.filterConf.andOperator) {
        this.filterConf.filters.forEach((fieldConf: any) => {
          if (fieldConf['search'].length > 0) {
            data = LocalFilter.filter(
              data,
              fieldConf['field'],
              fieldConf['search'],
              fieldConf['filter']
            );
          }
        });
      } else {
        let mergedData: any = [];
        this.filterConf.filters.forEach((fieldConf: any) => {
          if (fieldConf['search'].length > 0) {
            mergedData = mergedData.concat(
              LocalFilter.filter(
                data,
                fieldConf['field'],
                fieldConf['search'],
                fieldConf['filter']
              )
            );
          }
        });
        // remove non unique items
        data = mergedData.filter((elem: any, pos: any, arr: any) => {
          return arr.indexOf(elem) === pos;
        });
      }
    }
    return data;
  }

  protected paginate(data: any[]): any[] {
    if (
      this.pagingConf &&
      this.pagingConf['page'] &&
      this.pagingConf['perPage']
    ) {
      data = LocalPager.paginate(
        data,
        this.pagingConf['page'],
        this.pagingConf['perPage']
      );
    }
    return data;
  }
}
